import {
  Box,
  Heading,
  InlineStack,
  PanelSection,
  Paragraph,
  Toggle,
  Tooltip,
  TooltipContent,
} from '@customerio/pluma-components/react';

type IntegrationStateToggleProps = {
  isEnabled: boolean;
  isReadOnly: boolean;
  /**
   * Whether the connector cannot be switched on yet, which is also what the
   * tooltip explains. Each connector decides this for itself; today they all
   * mean "no events selected".
   */
  isEnableLocked: boolean;
  onChange: (isEnabled: boolean) => void;
};

/**
 * The "Integration state" row every connector settings form opens with: the
 * heading, the caveat about disabling, and the on/off toggle.
 */
export function IntegrationStateToggle({
  isEnabled,
  isReadOnly,
  isEnableLocked,
  onChange,
}: IntegrationStateToggleProps) {
  return (
    <PanelSection>
      <InlineStack alignBlock="center" alignInline="space-between">
        <Box flex={1} maxW="full" minW="0">
          <Heading level="2" text="product-h4" mb="100">
            Integration state
          </Heading>
          <Paragraph mb="200" color="subtle" size="xs">
            Note: If you disable, your integration settings will be saved, but no events will be sent.
          </Paragraph>
        </Box>

        {/*
          The tooltip explains why the toggle is dead, so it only exists while
          it is — and it anchors to the Toggle itself, whose field wrapper still
          fires pointer events while the input is disabled.
        */}
        <Tooltip isCustom isDisabled={!isEnableLocked}>
          {(context) => (
            <>
              <Toggle
                ref={context.reference.setReference}
                {...context.reference.getReferenceProps()}
                ariaLabel="Integration state"
                isChecked={isEnabled}
                isDisabled={isReadOnly || isEnableLocked}
                onChange={(event) => onChange(event.currentTarget.checked)}
                size="sm"
              />
              <TooltipContent>To enable, you must select at least one event to send.</TooltipContent>
            </>
          )}
        </Tooltip>
      </InlineStack>
    </PanelSection>
  );
}
